<script setup lang="ts">
// /ask — mockup page 2 (M3).
//
// Left: the eight symptom doors. Right: the expert panel that walks whichever
// tree you opened and then offers the free AI pass. Everything both halves
// say comes out of the tree contracts, so the page and the server agree by
// construction.
import { computed, nextTick, ref } from 'vue'
import { RouterLink } from 'vue-router'

import AskExpertPanel from '../components/AskExpertPanel.vue'
import ManualSection from '../sections/ManualSection.vue'
import TipsSection from '../sections/TipsSection.vue'
import { POPULAR, SYMPTOMS, TREES } from '../content/symptoms'
// Pixabay Content License — see src/assets/ATTRIBUTIONS.md.
import heroPhoto from '@/assets/askhvac-facade.jpg'

const treeId = ref<string | null>(null)
const activeId = ref<string | null>(null)
const seed = ref<string | undefined>(undefined)

const tree = computed(() => (treeId.value ? (TREES[treeId.value] ?? null) : null))
const activeSymptom = computed(() => SYMPTOMS.find((s) => s.id === activeId.value) ?? null)

async function open(nextTreeId: string, nextSeed?: string, symptomId?: string) {
    // Null first, and let it flush, so re-opening the same tree from another
    // card still restarts the walk — Vue would otherwise collapse
    // `x → null → x` into no change at all and leave the old answers on
    // screen. nextTick rather than requestAnimationFrame: rAF never fires in
    // a backgrounded tab, which would strand the panel.
    treeId.value = null
    activeId.value = symptomId ?? null
    await nextTick()
    treeId.value = nextTreeId
    seed.value = nextSeed
    if (window.matchMedia('(max-width: 1000px)').matches) {
        document.getElementById('expert')?.scrollIntoView({ behavior: 'smooth' })
    }
}

function clear() {
    treeId.value = null
    activeId.value = null
    seed.value = undefined
}

const HERO_BADGES = [
    { label: 'Guided questions', sub: 'Not a search box' },
    { label: 'Free AI analysis', sub: 'No signup, no email' },
    { label: 'Nothing to sell', sub: 'We install nothing' },
    { label: 'Independent guidance', sub: 'Your answers, our rules' },
]

const HOW = [
    {
        title: 'Describe your problem',
        body: 'Pick the symptom that fits, or type what your system is doing.',
    },
    {
        title: 'Answer a few questions',
        body: 'Each one rules something out — usually the cheap causes first.',
    },
    {
        title: 'Get the read',
        body: 'Likely causes, what to check yourself, and honest cost ranges.',
    },
    {
        title: 'Decide what to do',
        body: 'Fix it yourself, or hand the whole walk to a local pro.',
    },
]

const INSIGHTS = [
    {
        title: 'Possible causes',
        body: "What's most likely, and what's merely possible.",
        icon: '<path d="M10 1.8a5.4 5.4 0 0 0-3.1 9.8c.5.4.8 1 .8 1.6v.6h4.6v-.6c0-.6.3-1.2.8-1.6A5.4 5.4 0 0 0 10 1.8zM7.7 15.2h4.6v1.2H7.7zm.6 2.4h3.4l-.7.8H9z"/>',
    },
    {
        title: 'DIY checks',
        body: 'The things worth trying before anyone charges you.',
        icon: '<path d="M14.7 6.3a4 4 0 0 1-5.4 5.4l-5 5a1.9 1.9 0 0 1-2.7-2.7l5-5a4 4 0 0 1 5.4-5.4L9.6 5.9l1.2 3.3 3.3 1.2z"/>',
    },
    {
        title: 'Repair options',
        body: 'What the fix usually involves, and what it usually costs.',
        icon: '<path d="M10.9 2v1.4c1.7.2 2.9 1.1 3.1 2.7h-2.2c-.1-.6-.6-1-1.5-1-1 0-1.6.4-1.6 1s.4.9 1.9 1.2c2.4.5 3.5 1.3 3.5 3.1 0 1.7-1.2 2.8-3.2 3V15H9.2v-1.5c-2-.2-3.3-1.3-3.4-3h2.2c.1.8.8 1.3 1.9 1.3 1.1 0 1.7-.4 1.7-1.1 0-.6-.4-.9-2-1.3-2.3-.5-3.3-1.3-3.3-3C6.3 4.8 7.4 3.7 9.2 3.5V2z"/>',
    },
    {
        title: 'When to call a pro',
        body: 'Which jobs are licensed work, and why.',
        icon: '<path d="M4 2.8h12a1 1 0 0 1 1 1v12.4a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V3.8a1 1 0 0 1 1-1zm1.8 3.4v1.6h8.4V6.2zm0 3.6v1.6h8.4V9.8zm0 3.6v1.6h5.2v-1.6z"/>',
    },
    {
        title: 'Peace of mind',
        body: 'Enough context to tell a good quote from a bad one.',
        icon: '<path d="M10 1.6 3 4.6v5.2c0 4.3 3 8.3 7 9.2 4-.9 7-4.9 7-9.2V4.6zm-.9 12.1L5.8 10.4l1.3-1.3 2 2 4.1-4.1 1.3 1.3z"/>',
    },
]

const STILL_NEED = [
    'A free second opinion on a quote you already have',
    'No obligation — we install nothing',
    'Your walk goes with you, so you repeat nothing',
    'Local pros who work in Northeast Florida',
]
</script>

<template>
    <section class="hero" :style="{ backgroundImage: `url(${heroPhoto})` }">
        <div class="wash" aria-hidden="true"></div>
        <div class="hero-inner">
            <div>
                <p class="crumb">Home <span>›</span> Ask HVAC <span>›</span> Troubleshooter</p>
                <h1>
                    Ask Our <span class="hl">AI HVAC Expert</span><br />
                    Get Answers You Can Check
                </h1>
                <p class="lede">
                    Tell us what your AC is doing and we'll help you understand what's likely
                    causing it, what to check first, and what a fix should cost — before you call
                    anyone.
                </p>
                <ul class="badges">
                    <li v-for="b in HERO_BADGES" :key="b.label">
                        <strong>{{ b.label }}</strong>
                        <span>{{ b.sub }}</span>
                    </li>
                </ul>
            </div>

            <aside class="how">
                <h2>How It Works</h2>
                <ol>
                    <li v-for="(h, i) in HOW" :key="h.title">
                        <span class="n">{{ i + 1 }}</span>
                        <span class="t">
                            <strong>{{ h.title }}</strong>
                            <small>{{ h.body }}</small>
                        </span>
                    </li>
                </ol>
            </aside>
        </div>
    </section>

    <section class="main">
        <div class="main-inner">
            <div class="picker">
                <h2>What's going on with your AC system?</h2>
                <p class="sub">Select the issue that best describes what you're experiencing.</p>

                <div class="grid">
                    <button
                        v-for="s in SYMPTOMS"
                        :key="s.id"
                        type="button"
                        class="card"
                        :class="{ active: activeId === s.id }"
                        @click="open(s.treeId, s.seed, s.id)"
                    >
                        <span class="chip" :data-tone="s.tone ?? 'blue'">
                            <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" v-html="s.icon"></svg>
                        </span>
                        <span class="text">
                            <strong>{{ s.label }}</strong>
                            <small>{{ s.sub }}</small>
                        </span>
                    </button>
                </div>

                <div class="describe-bar">
                    <p>
                        <strong>Not sure what's wrong?</strong>
                        Describe the problem in your own words.
                    </p>
                    <button type="button" @click="open('askhvac.describe', undefined, 'something-else')">
                        Ask the AI →
                    </button>
                </div>

                <p v-if="activeSymptom" class="active-note">
                    Working on <strong>{{ activeSymptom.label }}</strong> in the panel
                    <span class="only-mobile">below</span><span class="only-desk">on the right</span>.
                </p>
            </div>

            <div id="expert" class="aside">
                <AskExpertPanel :tree="tree" :seed="seed" @select="(id, s) => open(id, s)" @clear="clear" />

                <div class="still">
                    <h3>Still need help?</h3>
                    <p>
                        Get a personalised assessment from a local HVAC expert. We hand over what
                        you told us — nothing more.
                    </p>
                    <ul>
                        <li v-for="item in STILL_NEED" :key="item">{{ item }}</li>
                    </ul>
                    <RouterLink class="cta" to="/second-opinion">Get My Free Second Opinion</RouterLink>
                    <p class="fine">100% free · No obligation · Local experts</p>
                </div>
            </div>
        </div>
    </section>

    <section class="insights">
        <div class="insights-inner">
            <h2>Get expert insight on</h2>
            <div class="insight-row">
                <div v-for="i in INSIGHTS" :key="i.title" class="insight">
                    <span class="chip">
                        <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" v-html="i.icon"></svg>
                    </span>
                    <strong>{{ i.title }}</strong>
                    <p>{{ i.body }}</p>
                </div>
            </div>
        </div>
    </section>

    <section class="popular">
        <div class="popular-inner">
            <h2>Popular AC problems homeowners ask about</h2>
            <p class="sub">
                Each one opens the same guided walk the panel above uses — the answer depends on
                your system, so we ask rather than guess.
            </p>
            <div class="popular-grid">
                <button
                    v-for="p in POPULAR"
                    :key="p.title"
                    type="button"
                    class="pop"
                    @click="open(p.treeId)"
                >
                    <strong>{{ p.title }}</strong>
                    <span>{{ p.blurb }}</span>
                    <span class="go">Walk me through it →</span>
                </button>
            </div>
        </div>
    </section>

    <ManualSection />
    <TipsSection />
</template>

<style scoped lang="scss">
/* ---- hero ---- */
.hero {
    position: relative;
    background-size: cover;
    background-position: center;
    color: #fff;
}

.wash {
    position: absolute;
    inset: 0;
    background: var(--site-gradient-hero-overlay);
}

.hero-inner {
    position: relative;
    z-index: 1;
    max-width: var(--site-content-max);
    margin: 0 auto;
    padding: var(--site-space-xl) var(--site-space-lg);
    display: grid;
    grid-template-columns: minmax(0, 1fr) 19rem;
    gap: var(--site-space-xl);
    align-items: center;

    @media (max-width: 960px) {
        grid-template-columns: 1fr;
    }
}

.crumb {
    margin: 0 0 var(--site-space-sm);
    font-size: 0.76rem;
    color: rgba(255, 255, 255, 0.62);

    span {
        margin: 0 0.3rem;
    }
}

h1 {
    font-family: var(--site-font-heading);
    font-size: clamp(1.7rem, 3.4vw, 2.6rem);
    font-weight: 800;
    line-height: 1.16;
    margin: 0 0 var(--site-space-md);
    text-shadow: 0 2px 18px rgba(3, 15, 40, 0.45);
}

.hl {
    color: var(--site-accent);
}

.lede {
    max-width: 36rem;
    margin: 0 0 var(--site-space-lg);
    color: rgba(255, 255, 255, 0.88);
    line-height: 1.6;
}

.badges {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: var(--site-space-md);

    li {
        display: flex;
        flex-direction: column;
        padding-left: 0.7rem;
        border-left: 2px solid var(--site-accent);
        line-height: 1.3;
    }

    strong {
        font-size: 0.84rem;
    }

    span {
        font-size: 0.72rem;
        color: rgba(255, 255, 255, 0.7);
    }
}

.how {
    background: rgba(7, 28, 69, 0.82);
    border: 1px solid rgba(255, 255, 255, 0.14);
    border-radius: var(--site-radius-md);
    padding: var(--site-space-md);

    h2 {
        font-family: var(--site-font-heading);
        font-size: 1rem;
        margin: 0 0 var(--site-space-sm);
        // The section rule below paints h2 navy for the light sections;
        // this one sits on navy.
        color: #fff;
    }

    ol {
        list-style: none;
        margin: 0;
        padding: 0;
        display: grid;
        gap: 0.65rem;
    }

    li {
        display: flex;
        gap: 0.6rem;
        align-items: flex-start;
    }

    .n {
        display: grid;
        place-items: center;
        width: 1.4rem;
        height: 1.4rem;
        flex-shrink: 0;
        border-radius: 50%;
        background: var(--site-blue);
        font-size: 0.72rem;
        font-weight: 700;
    }

    .t {
        display: flex;
        flex-direction: column;
        line-height: 1.35;
    }

    strong {
        font-size: 0.82rem;
    }

    small {
        font-size: 0.74rem;
        color: rgba(255, 255, 255, 0.72);
    }
}

/* ---- picker + panel ---- */
.main {
    background: var(--site-bg);
}

.main-inner {
    max-width: var(--site-content-max);
    margin: 0 auto;
    padding: var(--site-section-pad) var(--site-space-lg);
    display: grid;
    grid-template-columns: minmax(0, 1fr) 23rem;
    gap: var(--site-space-xl);
    align-items: start;

    @media (max-width: 1000px) {
        grid-template-columns: 1fr;
    }
}

h2 {
    font-family: var(--site-font-heading);
    font-size: clamp(1.3rem, 2.4vw, 1.7rem);
    font-weight: 700;
    color: var(--site-navy);
    margin: 0 0 0.3rem;
}

.sub {
    margin: 0 0 var(--site-space-lg);
    color: var(--site-text-muted);
    font-size: 0.92rem;
}

.grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
    gap: var(--site-space-md);
}

.card {
    display: flex;
    gap: 0.7rem;
    align-items: flex-start;
    text-align: left;
    padding: var(--site-space-md);
    background: var(--site-surface);
    border: 1px solid var(--site-border);
    border-radius: var(--site-radius-md);
    cursor: pointer;
    font-family: var(--site-font-body);
    transition: border-color 0.14s ease, box-shadow 0.14s ease, transform 0.14s ease;

    &:hover {
        border-color: var(--site-blue);
        transform: translateY(-2px);
        box-shadow: var(--site-shadow-md);
    }

    &.active {
        border-color: var(--site-accent);
        box-shadow: 0 0 0 1px var(--site-accent);
    }

    .chip {
        display: grid;
        place-items: center;
        width: 2.3rem;
        height: 2.3rem;
        flex-shrink: 0;
        border-radius: var(--site-radius-sm);
        background: var(--site-blue-soft);
        color: var(--site-blue);

        &[data-tone='orange'] {
            background: var(--site-accent-soft);
            color: var(--site-accent);
        }

        &[data-tone='green'] {
            background: var(--site-green-soft);
            color: var(--site-green);
        }

        svg {
            width: 1.25rem;
            height: 1.25rem;
        }
    }

    .text {
        display: flex;
        flex-direction: column;
        gap: 0.15rem;
    }

    strong {
        font-family: var(--site-font-heading);
        font-size: 0.94rem;
        color: var(--site-navy);
    }

    small {
        font-size: 0.78rem;
        color: var(--site-text-muted);
        line-height: 1.4;
    }
}

.describe-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--site-space-md);
    flex-wrap: wrap;
    margin-top: var(--site-space-md);
    padding: var(--site-space-md);
    background: var(--site-blue-soft);
    border: 1px solid var(--site-border);
    border-radius: var(--site-radius-md);

    p {
        margin: 0;
        font-size: 0.9rem;
        color: var(--site-text);
    }

    strong {
        color: var(--site-navy);
    }

    button {
        flex-shrink: 0;
        padding: 0.55rem 1.1rem;
        border: none;
        border-radius: var(--site-radius-sm);
        background: var(--site-blue);
        color: #fff;
        font-family: var(--site-font-body);
        font-weight: 700;
        font-size: 0.85rem;
        cursor: pointer;

        &:hover {
            background: var(--site-navy);
        }

        /* This is the door for anyone whose problem isn't one of the eight
           symptom cards — full width on a phone so it can't be missed. */
        @media (max-width: 860px) {
            width: 100%;
            min-height: 48px;
            font-size: 0.92rem;
        }
    }
}

.active-note {
    margin: var(--site-space-md) 0 0;
    font-size: 0.82rem;
    color: var(--site-text-muted);

    strong {
        color: var(--site-navy);
    }
}

.only-mobile {
    display: none;
}

@media (max-width: 1000px) {
    .only-mobile {
        display: inline;
    }

    .only-desk {
        display: none;
    }
}

.aside {
    display: grid;
    gap: var(--site-space-md);
    position: sticky;
    top: var(--site-space-md);

    @media (max-width: 1000px) {
        position: static;
    }
}

.still {
    padding: var(--site-space-lg);
    border-radius: var(--site-radius-md);
    background: var(--site-gradient-navy);
    color: #fff;

    h3 {
        font-family: var(--site-font-heading);
        font-size: 1.05rem;
        margin: 0 0 0.35rem;
    }

    p {
        margin: 0 0 var(--site-space-md);
        font-size: 0.85rem;
        color: rgba(255, 255, 255, 0.82);
        line-height: 1.5;
    }

    ul {
        list-style: none;
        margin: 0 0 var(--site-space-md);
        padding: 0;
        display: grid;
        gap: 0.4rem;
        font-size: 0.82rem;
    }

    li::before {
        content: '✓';
        margin-right: 0.45rem;
        color: #4ade80;
        font-weight: 700;
    }

    .cta {
        display: block;
        padding: 0.75rem 1rem;
        text-align: center;
        border-radius: var(--site-radius-sm);
        background: var(--site-gradient-cta);
        color: #fff;
        font-weight: 700;
        font-size: 0.92rem;
        text-decoration: none;

        &:hover {
            background: var(--site-accent-strong);
        }
    }

    .fine {
        margin: 0.5rem 0 0;
        text-align: center;
        font-size: 0.72rem;
        color: rgba(255, 255, 255, 0.65);
    }
}

/* ---- insights ---- */
.insights {
    background: var(--site-bg-alt);
}

.insights-inner {
    max-width: var(--site-content-max);
    margin: 0 auto;
    padding: var(--site-section-pad) var(--site-space-lg);
}

.insight-row {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: var(--site-space-lg);
    margin-top: var(--site-space-lg);
}

.insight {
    text-align: center;

    .chip {
        display: grid;
        place-items: center;
        width: 2.8rem;
        height: 2.8rem;
        margin: 0 auto 0.6rem;
        border-radius: var(--site-radius-md);
        background: var(--site-surface);
        border: 1px solid var(--site-border);
        color: var(--site-blue);

        svg {
            width: 1.4rem;
            height: 1.4rem;
        }
    }

    strong {
        display: block;
        font-family: var(--site-font-heading);
        font-size: 0.92rem;
        color: var(--site-navy);
        margin-bottom: 0.2rem;
    }

    p {
        margin: 0;
        font-size: 0.8rem;
        color: var(--site-text-muted);
        line-height: 1.5;
    }
}

/* ---- popular ---- */
.popular-inner {
    max-width: var(--site-content-max);
    margin: 0 auto;
    padding: var(--site-section-pad) var(--site-space-lg);
}

.popular-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
    gap: var(--site-space-md);
}

.pop {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    text-align: left;
    padding: var(--site-space-md);
    background: var(--site-surface);
    border: 1px solid var(--site-border);
    border-radius: var(--site-radius-md);
    cursor: pointer;
    font-family: var(--site-font-body);
    transition: border-color 0.14s ease, box-shadow 0.14s ease;

    &:hover {
        border-color: var(--site-blue);
        box-shadow: var(--site-shadow-md);
    }

    strong {
        font-family: var(--site-font-heading);
        font-size: 0.95rem;
        color: var(--site-navy);
        line-height: 1.3;
    }

    span {
        font-size: 0.82rem;
        color: var(--site-text-muted);
        line-height: 1.5;
    }

    .go {
        margin-top: 0.2rem;
        font-weight: 700;
        font-size: 0.8rem;
        color: var(--site-blue);
    }
}
</style>
